import React from "react";
import config from "../../../config";
import { WorkflowV1 } from "../interfaces/WorkflowV1";
import { handleResponseArray } from "./APIHandlerHelper";
import { ApiHandlerPureComponent } from "./ApiHandlerPureComponent";
import { WorkflowV2 } from "../interfaces/WorkflowV2";
import { MeasurementComponent } from "../enums/Result";

interface Props {
  onAuthError?: () => void;
}

export default class WorkflowApis extends React.PureComponent<Props> {
  private apiResponseHandler = new ApiHandlerPureComponent({
    onAuthError: this.props.onAuthError,
  });

  private static readonly BASE_URL: string = `${config.apiGateway.WORK_API}/api/Workflow/`;

  getWorkflowsByCustomer = (customerId: number): Promise<WorkflowV1[]> => {
    const apiAddress = `${WorkflowApis.BASE_URL}ByCustomerId/${customerId}`;

    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  getWorkflowsByCustomerAndCompany = (customerId: number, companyId: number): Promise<WorkflowV1[]> => {
    const apiAddress = `${WorkflowApis.BASE_URL}ByCustomerAndCompanyId/customerId/${customerId}/companyId/${companyId}`;

    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  getWorkflowsByCustomerAndSite = (customerId: number, siteId: number): Promise<WorkflowV1[]> => {
    const apiAddress = `${WorkflowApis.BASE_URL}ByCustomerAndSiteId/customerId/${customerId}/siteId/${siteId}`;

    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  getGenericWorkflowsByCustomer = (customerId: number): Promise<WorkflowV1[]> => {
    const apiAddress = `${WorkflowApis.BASE_URL}GenericByCustomerId/${customerId}`;

    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  getWorkflowsBySubstationAndCustomer = (substationId: number, customerId: number): Promise<WorkflowV1[]> => {
    const apiAddress = `${WorkflowApis.BASE_URL}ByCustomerAndSubstationId/CustomerId/${customerId}/SubstationId/${substationId}`;

    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  getWorkflowById = (workflowId: number): Promise<WorkflowV1 | WorkflowV2> => {
    const apiAddress = `${WorkflowApis.BASE_URL}${workflowId}`;

    return this.apiResponseHandler.get(apiAddress);
  };

  getWorkflowsPreviouslyUsedOnAsset = (assetId: number): Promise<WorkflowV1[]> => {
    const apiAddress = `${WorkflowApis.BASE_URL}ByPreviouslyUsedOnThisAsset/${assetId}`;
    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  createWorkflow = async (workflow: Partial<WorkflowV1 | WorkflowV2>): Promise<any> => {
    const apiAddress = WorkflowApis.BASE_URL;

    // Remove CircuitBreakerExpanded from the workflow before sending it to the API
    const updatedWorkflow = this._sanitizeWorkflow(workflow);

    return this.apiResponseHandler.post(apiAddress, JSON.stringify(updatedWorkflow), false);
  };

  updateWorkflow = (workflow: Partial<WorkflowV1> | Partial<WorkflowV2>): Promise<any> => {
    const apiAddress = WorkflowApis.BASE_URL;

    // Remove CircuitBreakerExpanded from the workflow before sending it to the API
    const updatedWorkflow = this._sanitizeWorkflow(workflow);

    return this.apiResponseHandler.put(apiAddress, JSON.stringify(updatedWorkflow), false);
  };

  deleteWorkflow = (workflowId: number): Promise<null> => {
    const apiAddress = `${WorkflowApis.BASE_URL}${workflowId}`;

    return this.apiResponseHandler.delete(apiAddress, false);
  };

  private _sanitizeWorkflow(workflow: Partial<WorkflowV1 | WorkflowV2>): Partial<WorkflowV1 | WorkflowV2> {
    return JSON.parse(JSON.stringify(workflow), (key, value) => {
      if (value === MeasurementComponent.CircuitBreakerExpanded) {
        return MeasurementComponent.CircuitBreaker;
      }
      return value;
    });
  }
}
